import React, { useState } from 'react';
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ConfirmDialog, ListSkeleton, SectionCard, StatusChip } from '../../components/ui';
import { useToast } from '../../contexts/ToastContext';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { tokens } from '../../theme/tokens';
import { systemJson } from './api';

interface RepairCheck {
  id: string;
  status: 'ok' | 'problem';
  code: string;
  params?: Record<string, string>;
  fix?: { action: 'remove_membership' | 'set_primary' | 'link_organization'; organizationId: string };
}

interface RepairReport {
  userId: string;
  email: string;
  displayName: string | null;
  userType: string | null;
  currentOrgId: string | null;
  primaryOrganizationId: string | null;
  organizationRoles: Array<{ orgId: string; role: string; isActive: boolean }>;
  checks: RepairCheck[];
}

export const RepairTab: React.FC = () => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const [term, setTerm] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [pending, setPending] = useState<RepairCheck | null>(null);

  const diagnose = useQuery({
    queryKey: ['admin-user-repair', submitted],
    enabled: Boolean(submitted),
    retry: false,
    queryFn: () => systemJson<RepairReport>(`/admin/repair/user-org/${encodeURIComponent(submitted)}`),
  });

  const fix = useMutation({
    mutationFn: (check: RepairCheck) =>
      systemJson(`/admin/repair/user-org/${encodeURIComponent(diagnose.data?.userId || submitted)}/fix`, {
        method: 'POST',
        body: JSON.stringify({ action: check.fix?.action, organizationId: check.fix?.organizationId }),
      }),
    onSuccess: () => {
      showToast(t('admin.systemV2.repair.fixed'), 'success');
      setPending(null);
      diagnose.refetch();
    },
    onError: (error: Error) => {
      showToast(error.message, 'error');
      setPending(null);
    },
  });

  const report = diagnose.data;
  const initials = (report?.displayName || report?.email || '?')
    .split(/\s+|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  const problems = report ? report.checks.filter((check) => check.status === 'problem').length : 0;
  const submit = () => {
    if (term.trim()) setSubmitted(term.trim());
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1080 }}>
      <SectionCard dense padded title={t('admin.systemV2.repair.title')} subtitle={t('admin.systemV2.repair.help')}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder={t('admin.systemV2.repair.placeholder')}
            inputProps={{ 'aria-label': t('admin.systemV2.repair.placeholder') }}
            sx={{ flex: '1 1 240px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: tokens.ink.faint }} />
                </InputAdornment>
              ),
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
          />
          <Button variant="contained" disabled={!term.trim()} onClick={submit}>
            {t('admin.systemV2.repair.diagnose')}
          </Button>
        </Box>
        {diagnose.isError && (
          <Typography sx={{ mt: 1.5, fontSize: 13.5, color: tokens.signal.problem.ink }}>{(diagnose.error as Error).message}</Typography>
        )}
      </SectionCard>

      {diagnose.isFetching && !report && (
        <SectionCard dense>
          <ListSkeleton rows={3} />
        </SectionCard>
      )}

      {report && (
        <SectionCard dense>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', px: '20px', py: 2, borderBottom: `1px solid ${tokens.line.hairline}` }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: tokens.accent.tintStrong, color: tokens.accent.hover, display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700, flex: '0 0 auto' }}>
              {initials}
            </Box>
            <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14.5, overflowWrap: 'anywhere' }}>{report.displayName || report.email}</Typography>
              <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, overflowWrap: 'anywhere' }}>
                {report.email} ·{' '}
                <Box component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 11.5 }}>{report.userId}</Box>
                {' · '}
                {report.userType || t('admin.systemV2.repair.unknownType')}
              </Typography>
            </Box>
            <StatusChip
              tone={problems ? 'caution' : 'positive'}
              label={problems ? t('admin.systemV2.repair.problemsFound', { count: problems }) : t('admin.systemV2.repair.noProblems')}
            />
          </Box>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {report.checks.map((check) => {
              const problem = check.status === 'problem';
              const tone = problem ? tokens.signal.caution : tokens.signal.positive;
              return (
                <Box
                  key={check.id}
                  component="li"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '22px minmax(0, 1fr)', sm: '22px minmax(0, 1fr) auto' },
                    gap: 1.5,
                    alignItems: 'center',
                    px: '20px',
                    py: 1.5,
                    borderTop: `1px solid ${tokens.line.hairline}`,
                    '&:first-of-type': { borderTop: 0 },
                  }}
                >
                  <Box
                    component="span"
                    role="img"
                    aria-label={problem ? t('admin.systemV2.repair.problem') : t('admin.systemV2.repair.ok')}
                    sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: tone.tint, color: tone.ink, border: `1px solid ${tone.border}`, display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700 }}
                  >
                    {problem ? '!' : '✓'}
                  </Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: problem ? 500 : 400, minWidth: 0 }}>
                    {t(`admin.systemV2.repair.checks.${check.code}`, check.params)}
                  </Typography>
                  {check.fix && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setPending(check)}
                      sx={{ gridColumn: { xs: '2', sm: 'auto' }, justifySelf: 'start', whiteSpace: 'nowrap' }}
                    >
                      {t(`admin.systemV2.repair.actions.${check.fix.action}`)}
                    </Button>
                  )}
                </Box>
              );
            })}
          </Box>
        </SectionCard>
      )}

      <ConfirmDialog
        open={Boolean(pending)}
        title={t('admin.systemV2.repair.confirmTitle')}
        message={pending ? t(`admin.systemV2.repair.checks.${pending.code}`, pending.params) : ''}
        confirmLabel={t('admin.systemV2.repair.confirm')}
        confirmColor={pending?.fix?.action === 'remove_membership' ? 'error' : 'primary'}
        loading={fix.isPending}
        onConfirm={() => pending && fix.mutate(pending)}
        onClose={() => setPending(null)}
      />
    </Box>
  );
};
